// Copyright (c) 2026 Yunus YILDIZ — SPDX-License-Identifier: BUSL-1.1

import { NavLink } from 'react-router-dom'
import {
  LayoutDashboard, FileText, Key, Webhook, Database,
  ScrollText, CreditCard, Settings, ChevronLeft, ChevronRight,
  BookOpen, ExternalLink, Bell,
} from 'lucide-react'
import { cn } from '@/lib/utils/cn'
import { ROUTES } from '@/constants/routes'

interface NavItem {
  label: string
  to: string
  icon: React.ElementType
  end?: boolean
}

const NAV_ITEMS: NavItem[] = [
  { label: 'Dashboard', to: ROUTES.DASHBOARD, icon: LayoutDashboard, end: true },
  { label: 'Documents', to: ROUTES.DOCUMENTS, icon: FileText },
  { label: 'API Keys', to: ROUTES.API_KEYS, icon: Key },
  { label: 'Webhooks', to: ROUTES.WEBHOOKS, icon: Webhook },
  { label: 'Schema Registry', to: ROUTES.SCHEMA_REGISTRY, icon: Database },
  { label: 'Audit Log', to: ROUTES.AUDIT_LOG, icon: ScrollText },
  { label: 'Notifications', to: ROUTES.NOTIFICATIONS, icon: Bell },
  { label: 'Billing', to: ROUTES.BILLING, icon: CreditCard },
  { label: 'Settings', to: ROUTES.SETTINGS, icon: Settings },
]

interface SidebarProps {
  collapsed: boolean
  onToggle: () => void
  onNavClick?: () => void
}

export function Sidebar({ collapsed, onToggle, onNavClick }: SidebarProps) {
  return (
    <aside
      data-sidebar
      className={cn(
        'relative flex flex-col border-r border-sidebar-border bg-sidebar-bg duration-200 ease-in-out',
        collapsed ? 'w-14' : 'w-56'
      )}
    >
      {/* Logo */}
      <button
        onClick={onToggle}
        className={cn(
          'flex min-h-12 h-12 items-center gap-2 border-b border-sidebar-border w-full hover:bg-sidebar-item-hover',
          collapsed ? 'justify-center px-2' : 'px-3',
        )}
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {collapsed ? (
          <img
            src="/etapsky_mark.svg"
            alt="Etapsky"
            className="h-8 w-8 shrink-0 object-contain"
          />
        ) : (
          <div className="flex w-full min-w-0 items-center gap-1.5 px-0">
            <span className="inline-grid min-h-0 min-w-0 flex-1 place-items-start [&>img]:col-start-1 [&>img]:row-start-1 [&>img]:max-w-full">
              <img
                src="/etapsky_horizontal_logo.svg"
                alt="Etapsky"
                className="h-8 w-auto max-w-full object-contain opacity-100 transition-opacity duration-200 ease-out dark:opacity-0"
              />
              <img
                src="/etapsky_horizontal_logo_on_dark.svg"
                alt=""
                className="h-8 w-auto max-w-full object-contain opacity-0 transition-opacity duration-200 ease-out dark:opacity-100"
                aria-hidden={true}
              />
            </span>
            <span className="rounded-full bg-gradient-to-r from-brand to-accent px-2 py-0.5 text-xs font-semibold text-white leading-none shrink-0">
              Portal
            </span>
          </div>
        )}
      </button>

      {/* Nav */}
      <nav className="flex-1 px-2 py-3 space-y-0.5">
        {NAV_ITEMS.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            onClick={onNavClick}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-2.5 rounded-md px-2 py-2 text-sm font-medium',
                collapsed && 'justify-center px-2',
                isActive
                  ? 'nav-item-active'
                  : 'text-ink-muted hover:bg-sidebar-item-hover hover:text-ink'
              )
            }
            title={collapsed ? item.label : undefined}
          >
            <item.icon className="h-4 w-4 shrink-0" />
            {!collapsed && <span>{item.label}</span>}
          </NavLink>
        ))}
      </nav>

      {/* Docs link */}
      <a
        href="https://docs.etapsky.com"
        target="_blank"
        rel="noopener noreferrer"
        className={cn(
          'mx-2 mb-2 flex items-center gap-2.5 rounded-md px-2 py-2 text-sm font-medium text-ink-muted hover:bg-sidebar-item-hover hover:text-ink',
          collapsed && 'justify-center'
        )}
        title={collapsed ? 'Documentation' : undefined}
      >
        <BookOpen className="h-4 w-4 shrink-0" />
        {!collapsed && (
          <>
            <span className="flex-1">Documentation</span>
            <ExternalLink className="h-3 w-3 opacity-40" />
          </>
        )}
      </a>

      {/* Collapse toggle */}
      <button
        onClick={onToggle}
        className="absolute -right-3 top-1/2 -translate-y-1/2 flex h-6 w-6 items-center justify-center rounded-full border border-line bg-raised shadow-sm text-ink-muted hover:text-ink z-10"
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {collapsed ? <ChevronRight className="h-3 w-3" /> : <ChevronLeft className="h-3 w-3" />}
      </button>
    </aside>
  )
}
